import { Controller } from "@hotwired/stimulus"

const SPINNER_SVG = `<svg class="w-8 h-8 animate-spin mb-4 text-brand" fill="none" viewBox="0 0 24 24">
  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
</svg>`

const SPINNER_SM = `<svg class="w-5 h-5 animate-spin text-brand" fill="none" viewBox="0 0 24 24">
  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
</svg>`

const CHECK_SVG = `<svg class="w-6 h-6 text-emerald-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
</svg>`

const WARN_SVG = `<svg class="w-6 h-6 text-amber-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
</svg>`

const ERROR_SVG = `<svg class="w-6 h-6 text-brand-red mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
</svg>`

export default class extends Controller {
  static targets = ["dateFrom", "dateTo", "campaign", "platform"]
  static values = { url: String }

  async apply(event) {
    event.preventDefault()
    const params = this.buildParams()
    const campaignId = params.get("campaign_id")
    const frame = document.getElementById("report_content")

    if (campaignId && frame) {
      this.showLoading(frame, campaignId)

      try {
        const token = document.querySelector("meta[name='csrf-token']")?.content
        const res = await fetch("/reports/sync_campaign", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": token
          },
          body: JSON.stringify({
            campaign_id: campaignId,
            date_from: params.get("date_from"),
            date_to: params.get("date_to")
          })
        })

        const data = await res.json()

        if (!res.ok) {
          this.showSyncError(frame, data.error || "Sync failed")
          return
        }

        if (data.synced === 0) {
          this.showSyncWarning(frame, campaignId, data)
          await this.delay(1500)
        } else {
          this.showSyncSuccess(frame, data)
          await this.delay(500)
        }
      } catch (e) {
        console.warn("[report-filter] Campaign sync failed:", e)
        this.showSyncError(frame, "Network error - could not sync campaign data")
        await this.delay(2000)
      }
    }

    const url = `${this.urlValue}?${params.toString()}`
    if (frame) {
      frame.src = url
    } else {
      window.location.href = url
    }
  }

  reset(event) {
    event.preventDefault()
    if (this.hasDateFromTarget) this.dateFromTarget.value = ""
    if (this.hasDateToTarget) this.dateToTarget.value = ""
    if (this.hasCampaignTarget) {
      this.campaignTarget.value = ""
      this.campaignTarget.dispatchEvent(new CustomEvent("reset"))
    }
    if (this.hasPlatformTarget) this.platformTarget.value = ""

    const frame = document.getElementById("report_content")
    if (frame) {
      frame.src = this.urlValue
    } else {
      window.location.href = this.urlValue
    }
  }

  // --- Private ---

  buildParams() {
    const params = new URLSearchParams()

    if (this.hasDateFromTarget && this.dateFromTarget.value) {
      params.set("date_from", this.dateFromTarget.value)
    }
    if (this.hasDateToTarget && this.dateToTarget.value) {
      params.set("date_to", this.dateToTarget.value)
    }
    if (this.hasCampaignTarget && this.campaignTarget.value) {
      params.set("campaign_id", this.campaignTarget.value)
    }
    if (this.hasPlatformTarget && this.platformTarget.value) {
      params.set("platform", this.platformTarget.value)
    }

    return params
  }

  showLoading(frame, campaignId) {
    const name = this.getCampaignName(campaignId)
    frame.innerHTML = `
      <div class="flex flex-col items-center justify-center py-24 text-gray-500 dark:text-gray-400">
        ${SPINNER_SVG}
        <span class="text-sm font-medium">Syncing data for: ${this.esc(name)}</span>
      </div>
    `
  }

  showSyncSuccess(frame, data) {
    frame.innerHTML = `
      <div class="flex flex-col items-center justify-center py-24 text-gray-500 dark:text-gray-400">
        ${CHECK_SVG}
        <span class="text-sm font-medium text-emerald-600 dark:text-emerald-400">Synced ${data.synced} records for: ${this.esc(data.campaign)}</span>
        <span class="text-xs text-gray-400 mt-1">${data.duration_ms}ms</span>
        <div class="mt-3">${SPINNER_SM}</div>
        <span class="text-xs text-gray-400 mt-1">Loading report...</span>
      </div>
    `
  }

  showSyncWarning(frame, campaignId, data) {
    const name = this.getCampaignName(campaignId)
    const reason = data.error || "No data returned from Meta for this date range"
    frame.innerHTML = `
      <div class="flex flex-col items-center justify-center py-24 text-gray-500 dark:text-gray-400">
        ${WARN_SVG}
        <span class="text-sm font-medium text-amber-600 dark:text-amber-400">${this.esc(reason)}</span>
        <span class="text-xs text-gray-400 mt-1">Loading cached results for: ${this.esc(name)}</span>
        <div class="mt-3">${SPINNER_SM}</div>
      </div>
    `
  }

  showSyncError(frame, message) {
    frame.innerHTML = `
      <div class="flex flex-col items-center justify-center py-24 text-gray-500 dark:text-gray-400">
        ${ERROR_SVG}
        <span class="text-sm font-medium text-brand-red">${this.esc(message)}</span>
        <span class="text-xs text-gray-400 mt-2">Try again or select a different campaign</span>
      </div>
    `
  }

  getCampaignName(campaignId) {
    const label = document.querySelector("[data-campaign-dropdown-target='triggerLabel']")
    const text = label?.textContent?.trim()
    return text && text !== "All campaigns" ? text : campaignId
  }

  delay(ms) {
    return new Promise(r => setTimeout(r, ms))
  }

  esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML
  }
}
